import { MOBILE } from '../../assets/scripts/constants';
import PageScroll from '../../assets/scripts/helpers/Scroll';
import Tabs from '../../components/tabs/tabs';
import Menu from '../menu/menu';

const HTML_CLASSLIST = document.documentElement.classList;

class Search {
	constructor() {
		this.init();
	}

	init() {
		this.container = document.querySelector('[data-search]');

		if (!this.container) return;

		this._initSearch();
	}

	_initSearch() {
		this.openers = Array.from(document.querySelectorAll('[data-search-opener]'));
		this.searchInput = this.container.querySelector('.search__input input');
		this.loader = this.container.querySelector('.search__loader');
		this.empty = this.container.querySelector('.search__empty-wrapper');
		this.form = this.container.querySelector('form');
		this.isOpened = false;
		this.isDefaultState = true;
		this.activeItemIndex = null;
		this.items = [];

		if (!this.searchInput) {
			return;
		}

		this.onWindowClick = this._handleWindowClick.bind(this);
		this.onWindowKeydown = this._handleWindowKeydown.bind(this);

		const mm = window.matchMedia(MOBILE);
		const onMMChange = () => {
			if (mm.matches) {
				this.close();
			}
		};
		onMMChange();
		mm.addEventListener('change', onMMChange);

		this.openers.forEach((opener) => {
			opener.addEventListener('click', (evt) => {
				evt.preventDefault();

				if (mm.matches) {
					Menu.open();
					Menu.showSearch();
				} else {
					this.toggle();
				}
			});
		});

		this.form.addEventListener(
			'submit',
			(evt) => {
				evt.preventDefault();
				evt.stopPropagation();
				evt.stopImmediatePropagation();
			},
			true,
		);

		this.updateItems();
		this.searchInput.addEventListener('keydown', this._handleInputKeydown.bind(this));
	}

	_handleWindowClick(evt) {
		const isOpener = this.openers.some((opener) => opener.contains(evt.target));

		if (!this.container.contains(evt.target) && !isOpener) {
			this.close();
		}
	}

	_handleWindowKeydown(evt) {
		if (evt.key === 'Escape') {
			this.close();
		}
	}

	_handleInputKeydown(evt) {
		if (this.items.length === 0) {
			return;
		}

		switch (evt.key) {
			case 'ArrowDown':
				evt.preventDefault();
				this._navigateDown();
				break;
			case 'ArrowUp':
				evt.preventDefault();
				this._navigateUp();
				break;
			case 'Enter':
				evt.preventDefault();
				this._enterActiveItem();
				break;
			case 'Tab':
				this.activeItemIndex = null;
				this._updateItemsClass();
				break;
		}
	}

	_navigateDown() {
		if (this.activeItemIndex === null) {
			this.activeItemIndex = 0;
		} else {
			this.activeItemIndex = (this.activeItemIndex + 1) % this.items.length;
		}
		this._updateItemsClass();
		this._scrollToSelectedItem();
	}

	_navigateUp() {
		if (this.activeItemIndex === null) {
			this.activeItemIndex = this.items.length - 1;
		} else {
			this.activeItemIndex = (this.activeItemIndex - 1 + this.items.length) % this.items.length;
		}

		this._updateItemsClass();
		this._scrollToSelectedItem();
	}

	_enterActiveItem() {
		const activeElement = this.items[this.activeItemIndex];

		if (!activeElement) {
			return;
		}

		const isElementLink = activeElement.tagName.toLowerCase() === 'a' && activeElement.href;

		if (isElementLink) {
			window.location.href = activeElement.href;
		}
	}

	_updateItemsClass() {
		this.items.forEach((element, index) => {
			element.classList.remove('_focus');

			if (this.activeItemIndex === index) {
				element.classList.add('_focus');
			}
		});
	}

	_scrollToSelectedItem() {
		const activeElement = this.items[this.activeItemIndex];

		if (!activeElement) {
			return;
		}

		switch (this.activeItemIndex) {
			case 0:
				this.searchItemsContainer.scrollTo(0, 0);
				break;
			case this.items.length - 1:
				this.searchItemsContainer.scrollTo(0, this.searchItemsContainer.scrollHeight);
				break;

			default:
				activeElement.scrollIntoView({ block: 'nearest' });
				break;
		}
	}

	open() {
		PageScroll.disabled();
		this.openers.forEach((opener) => opener.classList.add('_active'));
		this.container.classList.add('_active');
		HTML_CLASSLIST.add('_search-opened');
		this.isOpened = true;
		this.activeItemIndex = null;
		this.searchInput.value = '';
		this.searchInput.dispatchEvent(new Event('change'));

		setTimeout(() => {
			this.searchInput.focus();
		}, 100);

		window.addEventListener('click', this.onWindowClick);
		window.addEventListener('keydown', this.onWindowKeydown);
	}

	close() {
		this.openers.forEach((opener) => opener.classList.remove('_active'));
		this.container.classList.remove('_active');
		HTML_CLASSLIST.remove('_search-opened');
		PageScroll.enabled();
		this.isOpened = false;
		this.activeItemIndex = null;
		this.hideEmpty();
		this.hideLoader();
		this.showDefaultState();

		setTimeout(() => {
			this.openers[0]?.focus();
		}, 500);

		window.removeEventListener('click', this.onWindowClick);
		window.removeEventListener('keydown', this.onWindowKeydown);
	}

	toggle() {
		this.isOpened ? this.close() : this.open();
	}

	showLoader() {
		this.loader.classList.remove('_hidden');
		this.container.classList.add('_loading');
	}

	hideLoader() {
		this.loader.classList.add('_hidden');
		this.container.classList.remove('_loading');
	}

	showEmpty() {
		this.empty.classList.remove('_hidden');
		this.container.classList.add('_empty');
	}

	hideEmpty() {
		this.empty.classList.add('_hidden');
		this.container.classList.remove('_empty');
	}

	showDefaultState() {
		const defaultResult = this.container.querySelector('.search__result._default');
		const tabList = this.container.querySelector('.search__tabs');

		defaultResult.classList.remove('_hidden');
		tabList.classList.add('_hidden');
		this.isDefaultState = true;
	}

	hideDefaultState() {
		const defaultResult = this.container.querySelector('.search__result._default');
		const tabList = this.container.querySelector('.search__tabs');

		defaultResult.classList.add('_hidden');
		tabList.classList.remove('_hidden');
		this.isDefaultState = false;
	}

	initTabs() {
		const tabs = this.container.querySelector('[data-tabs]');

		if (tabs && tabs.tabsInstance) {
			tabs.tabsInstance.destroy();
			delete tabs.tabsInstance;
		}

		if (tabs) {
			tabs.tabsInstance = new Tabs(tabs);
		}
	}

	updateItems() {
		const searchContainer = this.isDefaultState
			? this.container.querySelector('.search__result._default')
			: this.container.querySelector('[data-tab-panel]');
		if (!searchContainer) {
			return;
		}
		this.searchItemsContainer = searchContainer;
		this.items = Array.from(searchContainer.querySelectorAll('.search__result-list a'));

		this.activeItemIndex = null;

		this.items.forEach((item) => {
			item.addEventListener('mouseenter', () => {
				this.activeItemIndex = null;
				this._updateItemsClass();
			});
			item.addEventListener('mouseleave', () => {
				this.activeItemIndex = null;
				this._updateItemsClass();
			});
		});
	}
}

export default new Search();
